import { v4 as uuid } from "uuid";
import { AssetType, AuthoringBlockType, BackgroundStyleType, ElementTextBlockPositionType, ForeColorType, FormatType, HeaderPositionType, PaletteColorType, TextStyleType, TrayType } from "../constants";
import { iconSearch } from "../iconSearch";

export class SlideModelBuilder {
    constructor(type, slideData, isFirstChoice) {
        this.type = type;
        this.slideData = slideData;
        this.isFirstChoice = isFirstChoice;

        this.model = {
            template_id: type,
            layout: {
                headerPosition: HeaderPositionType.NONE,
                trayLayout: TrayType.NONE,
                elementTextBlockPosition: ElementTextBlockPositionType.NONE
            },
            states: [{}],
            version: 10,
            migrationVersion: 10.03,
            textStylesVersion: 2,
            metadata: {
                wasAiGenerated: true,
                needsLayoutProcessing: true
            },
            hasBeenEdited: true
        };
    }

    validateEnum(value, validValues, defaultValue) {
        if (Object.values(validValues).includes(value)) {
            return value;
        }
        return defaultValue;
    }

    cleanUpString(value) {
        if (typeof value === "string") {
            return value.trim();
        } else if (Array.isArray(value)) {
            return value.map(b => b.html).join("\n");
        } else {
            return value.html ?? "";
        }
    }

    cleanUpRichText(textModel) {
        let minIndent = 0;
        for (let block of textModel) {
            if (block.textStyle === TextStyleType.HEADING) {
                block.textStyle = TextStyleType.TITLE;
            }
            if (block.textStyle === TextStyleType.BULLET_LIST) {
                minIndent = Math.min(minIndent, block.indent ?? 0);
            }
            if ([TextStyleType.TITLE, TextStyleType.LABEL, TextStyleType.BODY, TextStyleType.BULLET_LIST, TextStyleType.HEADING].includes(block.textStyle) === false) {
                block.textStyle = TextStyleType.BODY;
            }
            block.html = block.html.replaceAll(/<strong>(.*?)<\/strong>/g, '<font class="emphasized">$1</font>');
        }
        if (minIndent > 0) {
            for (let block of textModel) {
                if (block.textStyle === TextStyleType.BULLET_LIST) {
                    block.indent = (block.indent ?? minIndent) - minIndent;
                }
            }
        }
        return textModel;
    }

    parseFormat(format) {
        if (format == "percent" || format == "percentage") {
            return FormatType.PERCENT;
        } else if (format == "currency") {
            return FormatType.CURRENCY;
        } else if (format == "number") {
            return FormatType.NUMBER;
        } else if (format == "date") {
            return FormatType.DATE;
        } else {
            return FormatType.NONE;
        }
    }

    setBackgroundColor(color) {
        this.model.layout.backgroundColor = color;
        this.model.layout.backgroundStyle = BackgroundStyleType.COLOR;
        return this.model.layout.backgroundColor;
    }

    createHeader({ heading = this.slideData.slideTitle, label, subHeading = this.slideData.slideSubTitle, position = HeaderPositionType.TOP, backgroundColor } = {}) {
        this.model.layout.headerPosition = position;
        this.model.states[0].header = {
            backgroundColor,
            text: {
                blocks: [
                    this.createBlock(label, TextStyleType.LABEL),
                    this.createBlock(heading, TextStyleType.HEADING),
                    this.createBlock(subHeading, TextStyleType.BODY),
                ].filter(b => b)
            }
        };
        return this.model.states[0].header;
    }

    createSingleBlockText(html, textStyle = "_use_styles_", props = {}) {
        if (html) {
            if (Array.isArray(html)) {
                html = html.map(b => b.html).join("\n");
            }

            return {
                blocks: [
                    this.createBlock(html, textStyle, props)
                ].filter(b => b)
            };
        }
    }

    createBlock(html, textStyle = "_use_styles_", props = {}) {
        if (html) {
            if (Array.isArray(html)) {
                html = html.map(b => b.html).join("\n");
            } else if (html.html) {
                html = html.html;
            }

            return {
                id: uuid(),
                html,
                type: AuthoringBlockType.TEXT,
                textStyle,
                ...props,
            };
        }
    }

    createMediaBlock(icon, height = 100, props = {}) {
        if (icon) {
            return {
                id: uuid(),
                html: "",
                type: AuthoringBlockType.MEDIA,
                blockHeight: height,
                elementModel: {
                    elementType: "MediaBlock",
                    ...this.createIcon(icon),
                    iconScale: 1.5,
                    frameType: "none",
                    color: ForeColorType.PRIMARY,
                    ...props
                },
            };
        }
    }

    createPrimary(model) {
        this.model.states[0].primary = model;
        return this.model.states[0].primary;
    }

    createIcon(search) {
        if (search && search.length > 0) {
            let response = iconSearch(search);

            if (response.results && response.results.length > 0) {
                return {
                    content_type: "icon",
                    content_value: response.results[0].ref
                };
            } else {
                return {
                    content_type: "icon",
                    content_value: "star"
                };
            }
        } else {
            return {
                content_type: "icon",
                content_value: "star"
            };
        }
    }

    createImage(imagePrompt, props = {}, isSlideImage = false) {
        let image = {
            ...props,
            __generateImage: {
                search: imagePrompt,
                assetType: AssetType.IMAGE,
                isGenerating: true,
                imageModel: this.slideData.slideImage?.imageModel,
                imageQuality: this.slideData.slideImage?.imageQuality,
                imageStyle: this.slideData.slideImage?.imageStyle,
                imageSource: this.slideData.slideImage?.imageSource,
                isSlideImage
            }
        };

        if (isSlideImage && this.slideData.slideImage?.assetId) {
            image.content_type = AssetType.IMAGE;
            image.content_value = this.slideData.slideImage.assetId;
            image.__generateImage.isGenerating = false;
        }

        return image;
    }

    createLogo(logo, props = {}) {
        let image = {
            ...props,
            __generateImage: {
                search: logo,
                assetType: AssetType.LOGO,
                isGenerating: true
            }
        };
        return image;
    }

    createSideTray({ imagePrompt, trayType = TrayType.LEFT_TRAY, trayWidth = 300 }, useSlideImage = false) {
        this.model.layout.trayLayout = trayType;

        this.model.states[0].tray = {
            gridLayout: { cols: 12, rows: 12, cells: [{ x: 0, y: 0, width: 12, height: 12 }] },
            trayWidth,
            items: [{
                componentType: "TextAndImage",
                childElement: this.createImage(imagePrompt, {}, useSlideImage)
            }]
        };
    }

    createBottomTray(text, trayType = ElementTextBlockPositionType.TRAY) {
        if (!text || text.length == 0) return;

        this.model.layout.elementTextBlockPosition = trayType;

        if (trayType == ElementTextBlockPositionType.INLINE) {
            this.model.states[0].elementTextBlock = {
                // trayHeight: 75,
                elementTextBlock: {
                    blocks: [
                        this.createBlock(text, TextStyleType.TITLE)
                    ]
                }
            };
        } else {
            this.model.states[0].elementTextBlock = {
                // trayHeight: 75,
                items: [{
                    componentType: "TextAndImage",
                    cellColor: PaletteColorType.BACKGROUND_ACCENT,
                    childElement: {
                        text: {
                            blocks: [
                                this.createBlock(text, TextStyleType.TITLE)
                            ].filter(b => b)
                        }
                    }
                }],
            };
        }
    }

    getModel() {
        return this.model;
    }
}
